import { getDownloader } from './Downloader';
import { ElementNode } from './ElementNode';
import type { CrossOrigin } from './Def';

export interface StyleNodeInitOptions {
    owner?: string;
    disabled?: boolean;
    media?: string;
    href?: string;
    content?: string;
    rel?: string;
    type?: string;
    crossOrigin?: CrossOrigin;
}

export class StyleNode extends ElementNode {
    #options: StyleNodeInitOptions;

    constructor(options: StyleNodeInitOptions) {
        super({ owner: options.owner || location.href });
        this.#options = options;
    }

    public override get [Symbol.toStringTag](): string {
        return 'StyleNode';
    }

    public get isInline(): boolean {
        return !this.#options.href;
    }

    public get crossOrigin(): CrossOrigin {
        return this.#options.crossOrigin;
    }

    public get href(): string | undefined {
        return this.#options.href?.trim();
    }

    public get media(): string | undefined {
        return this.#options.media;
    }

    public override get content(): string {
        return (this.#options.content || '').trim();
    }

    public downloadContent(options?: RequestInit): Promise<string> {
        if (!this.href) {
            return Promise.resolve(this.content);
        }

        const req: RequestInit = {
            ...options,
        };

        if (this.#options.crossOrigin === 'use-credentials') {
            req.credentials = 'include';
        }

        return getDownloader()
            .download(this.href, req)
            .then(text => {
                this.#options.content = text;
                return text;
            });
    }

    public override get isValid(): boolean {
        if (this.#options.disabled) {
            return false;
        }

        if (!['', undefined, 'stylesheet'].includes(this.#options.rel)) {
            return false;
        }

        if (!this.#options.href && !this.content) {
            return false;
        }

        if (!['', undefined, 'text/css'].includes(this.#options.type)) {
            return false;
        }

        return true;
    }

    public override toString(): string {
        if (this.isInline) {
            return `<${['style', this.#options.type ? `type=${this.#options.type}` : ''].filter(Boolean).join(' ')}>${
                this.content.length > 50 ? `${this.content.slice(0, 50)}...` : this.content
            }</style>`;
        }
        return `<${[
            'link',
            this.href ? `href="${this.href}"` : '',
            this.#options.type ? `type=${this.#options.type}` : '',
            this.#options.rel ? `ref="${this.#options.rel}"` : '',
            this.#options.crossOrigin ? `crossorigin="${this.#options.crossOrigin}"` : '',
        ]
            .filter(Boolean)
            .join(' ')}></link>`;
    }
}
